import { HttpEvent, HttpEventType, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { from, Observable, throwError } from 'rxjs';
import { catchError, mergeMap, tap } from 'rxjs/operators';
import { RepoAuthService } from '../../data/repositories/repo-auth.service';
import { AppSettingsService } from './app-settings.service';
import { AccountExpiredError } from './bx-api-error.service';
import { BxLogger } from './bx-logger.service';

@Injectable({
    providedIn: 'root',
})
export class HttpInterceptorService implements HttpInterceptor {
    constructor(@Inject(RepoAuthService) private authService: RepoAuthService) {}
    router = inject(Router);
    logger = inject(BxLogger);
    appSettings = inject(AppSettingsService);

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return from(this.authService.getAccessToken()).pipe(
            mergeMap((token) => {
                if (token && this.isRequestValid(request)) {
                    request = request.clone({ headers: request.headers.set('Authorization', 'Bearer ' + token) });
                }

                // If we are sending file via graphql we should only add authentication
                if (request.body instanceof FormData && (request.url.endsWith('graphql') || request.url.endsWith('graphql-v2'))) {
                    request = request.clone({ headers: request.headers.set('GraphQL-preflight', '1') });
                    return this.handleNext(request, next);
                }
                if (!request.headers.has('Content-Type')) {
                    request = request.clone({ headers: request.headers.set('Content-Type', 'application/json') });
                }

                // for multipart form-data, the content type needs to be undefined to add the correct boundaries
                if (request.headers.get('Content-Type') === 'multipart/form-data') {
                    request = request.clone({ headers: request.headers.delete('Content-Type', 'multipart/form-data') });
                }

                request = request.clone({ headers: request.headers.set('Accept', 'application/json') });

                return this.handleNext(request, next);
            })
        );
    }

    isRequestValid(request: HttpRequest<any>): boolean {
        // @ts-ignore TS2345
        return request.url.indexOf(window.APP_CONFIG.globalApiEndpoint) >= 0;
    }

    handleNext<T>(request: HttpRequest<T>, next: HttpHandler) {
        return next.handle(request).pipe(
            tap((event) => {
                if (event.type === HttpEventType.Response) {
                    const errors = event?.body?.errors;
                    errors?.forEach((error: { extensions: { code: string } }) => {
                        if (error.extensions.code === AccountExpiredError.code) {
                            this.logger.warning('Please subscribe to ' + this.appSettings.app.name);
                            this.router.navigateByUrl('/account/subscription');
                        }
                    });
                }
            }),
            catchError((err) => throwError(err))
        );
    }
}
